"use client";

import type { ComponentProps } from "react";

import { useResolveI18n } from "./resolveI18n";

import { Avatar, AvatarFallback } from "@/components/ui/v2/avatar";
import { Badge } from "@/components/ui/v2/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { cn } from "@/lib/utils/cn";

type CardItem = {
  title: string;
  description?: string;
  status?: string;
  avatar?: string;
  tags?: string[];
  inactiveTags?: string[];
};

type InfoCardProps = Omit<ComponentProps<"div">, "children" | "title"> & {
  title?: string;
  cards: readonly CardItem[];
};

const MAX_VISIBLE_TAGS = 3;

function isActiveStatus(status: string): boolean {
  const lower = status.toLowerCase();
  return lower === "active" || lower === "actief";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge variant={isActiveStatus(status) ? "success" : "neutral"} size="medium">
      {status}
    </Badge>
  );
}

function SingleCard({ card, index }: { card: CardItem; index: number }) {
  const resolve = useResolveI18n();

  const title = resolve(card.title);
  const description = card.description ? resolve(card.description) : undefined;

  const avatarText =
    card.avatar ??
    title
      .split(/\s+/)
      .map((w) => w[0])
      .join("")
      .toUpperCase();

  const allTags = [
    ...(card.tags ?? []).map((tag) => ({ name: resolve(tag), active: true })),
    ...(card.inactiveTags ?? []).map((tag) => ({ name: resolve(tag), active: false })),
  ];
  const visibleTags = allTags.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = allTags.length - MAX_VISIBLE_TAGS;

  return (
    <Card
      className="min-h-[172px] justify-between animate-in fade-in fill-mode-both duration-300"
      style={{ animationDelay: `${index * 60}ms` }}
      padding="sm"
    >
      <CardHeader className="flex flex-row flex-wrap items-start gap-4">
        <Avatar className="shrink-0">
          <AvatarFallback>{avatarText}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="min-w-0 truncate text-base font-semibold leading-6">
              {title}
            </CardTitle>
            {card.status && (
              <span className="shrink-0">
                <StatusBadge status={card.status} />
              </span>
            )}
          </div>
          {description && (
            <CardDescription className="line-clamp-2">
              {description}
            </CardDescription>
          )}
        </div>
      </CardHeader>

      {allTags.length > 0 && (
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            {visibleTags.map((tag) => (
              <Badge key={tag.name} variant={tag.active ? "success" : "neutral"}>
                {tag.name}
              </Badge>
            ))}
            {overflowCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="cursor-default">
                    +{overflowCount} more
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-1">
                    {allTags.slice(MAX_VISIBLE_TAGS).map((tag) => (
                      <span key={tag.name}>
                        {tag.name} ({tag.active ? "Active" : "Inactive"})
                      </span>
                    ))}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export function InfoCard({ title, cards, className, ...props }: InfoCardProps) {
  const useGrid = cards.length > 3;

  return (
    <div className={cn("flex w-full flex-col gap-2", className)} {...props}>
      {title && (
        <span className="text-sm font-semibold text-foreground">{title}</span>
      )}
      <div
        className={cn(
          useGrid
            ? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
            : "flex flex-col gap-2",
        )}
      >
        {cards.map((card, i) => (
          <SingleCard key={card.title} card={card} index={i} />
        ))}
      </div>
    </div>
  );
}
